import moment from 'moment'
import { Column } from 'primereact/column'
import { DataTable, DataTablePFSEvent } from 'primereact/datatable'
import { Dropdown } from 'primereact/dropdown'
import { InputText } from 'primereact/inputtext'
import { OverlayPanel } from 'primereact/overlaypanel'
import { classNames } from 'primereact/utils'
import { useState } from 'react'

// local imports
import noRecordImg from 'assets/icons/no-content.png'
import warningImage from 'assets/images/warning-image.svg'
import NotificationDialog from 'components/ui/NotificationDialog'
import 'pages/Tracing.scss'
import NotificationService from '../../service/NotificationService'
import RequestHomeOfficeService from '../../service/RequestHomeOfficeService'
import { IEmployeeSummary } from '../../types/Employee'
import {
  EReservationStatus,
  IChangeReservationRequest,
  IRemoteReservationResponse,
  ReservationStatus,
} from '../../types/RemoteReservationRequest'
import useNotificationTable from './hooks/use-notification-table'
import { ApprovalButtonGroup, TableApproveButton, TableRejectButton } from './styled'

interface ComponentProps {
  data: IRemoteReservationResponse[] | undefined
  selectedStatus: ReservationStatus[]
  getTableData: () => void
  loading: boolean
  startDate: Date
  endDate: Date
}

export default function NotificationTable({
  data,
  selectedStatus,
  getTableData,
  startDate,
  endDate,
  loading,
}: ComponentProps) {
  const {
    exportExcel,
    globalFilterValue,
    hasItems,
    approveNotification,
    rejectNotification,
    notification,
    onGlobalFilterChange,
    selectedRow,
    setApproveNotification,
    setRejectNotification,
    setNotification,
    setSelectedRow,
    showExcel,
    tableData,
    t,
    isStatusOptions,
    addToExcel,
    filters,
  } = useNotificationTable(data, selectedStatus)

  /* Custom Pagination */
  const [first, setFirst] = useState(0)
  const [rows, setRows] = useState(10)
  const [inputValue, setInputValue] = useState<string>('')
  const [actionLoading, setActionLoading] = useState(false)
  const [rejectValidationError, setRejectValidationError] = useState(false)
  const handlePageChange = (e: DataTablePFSEvent) => {
    setFirst(e.first)
    setRows(e.rows)
  }

  /* IsCancel Button Handle Change */
  const handleConfirmIsCanceled = (rowData: IRemoteReservationResponse | undefined) => {
    if (!rowData) return
    if (!inputValue.trim()) {
      setRejectValidationError(true)
      return
    }
    setRejectValidationError(false)
    setActionLoading(true)
    const reqParam: IChangeReservationRequest | any = {
      managerId: managerId(),
      id: selectedRow?.id,
      reservationId: selectedRow?.reservationId,
      statusId: EReservationStatus.Rejected,
      rejectionExplanation: inputValue,
    }
    RequestHomeOfficeService.changeRequestStatus(reqParam)
      .then((_res) => {
        getTableData()
        setRejectNotification(false)
        // Dispatch custom event to update badge count
        window.dispatchEvent(new CustomEvent('updatePendingApprovalCount', { detail: { startDate, endDate } }))
        // Interceptor'daki toast'ı temizle
        if ((window as any).primeToast?.current) {
          ;(window as any).primeToast.current.clear()
        }
        // Başarı popup'ı göster
        NotificationService.showCustomPopup(
          t('success-text.remote-office-rejected'),
          'success',
          t('success-text.success'),
          t('buttons.ok')
        )
      })
      .catch((error) => {
        // Dialog'u kapat
        setRejectNotification(false)
        // Interceptor hata popup'ı gösterecek
      })
      .finally(() => setActionLoading(false))
  }

  /* Handle Multiple IsCancel Update */

  const handleConfirmApprove = () => {
    if (!selectedRow) return
    setActionLoading(true)
    const reqParam: IChangeReservationRequest | any = {
      managerId: managerId(),
      id: selectedRow?.id,
      reservationId: selectedRow?.reservationId,
      statusId: EReservationStatus.Approved,
    }
    RequestHomeOfficeService.changeRequestStatus(reqParam)
      .then((_res) => {
        getTableData()
        setApproveNotification(false)
        // Dispatch custom event to update badge count
        window.dispatchEvent(new CustomEvent('updatePendingApprovalCount', { detail: { startDate, endDate } }))
        // Başarı popup'ı göster
        NotificationService.showCustomPopup(
          t('success-text.remote-office-approved'),
          'success',
          t('success-text.success'),
          t('buttons.ok')
        )
      })
      .catch((error) => {
        // Dialog'u kapat
        setApproveNotification(false)
        // Interceptor hata popup'ı gösterecek
      })
      .finally(() => setActionLoading(false))
  }
  const managerId = () => {
    const item = localStorage.getItem('employee')
    if (item) {
      const loginEmployee = JSON.parse(item) as IEmployeeSummary
      return loginEmployee.firmEmployeeId
    }
    return 0
  }
  const formatNotificationText = (notificationCount: string | undefined): string => {
    if (!notificationCount || notificationCount === '-') return ''
    // "Bu ay 2. Bildirim" -> "bu ay 2. bildirimi"
    // "Bu hafta 2. Bildirim" -> "bu hafta 2. bildirimi"
    // "Bu hafta 2. Bildirim olacak" -> "bu hafta 2. bildirimi olacak"
    return notificationCount.toLowerCase().replace('bildirim', 'bildirimi')
  }

  const onApproveClick = async (data: IRemoteReservationResponse) => {
    setSelectedRow(data)
    setApproveNotification(true)
  }
  const onRejectClick = async (data: IRemoteReservationResponse) => {
    setSelectedRow(data)
    setRejectNotification(true)
    setInputValue('')
    setRejectValidationError(false)
  }
  /* ############# Table Templates #############  */
  const tableHeaderTemplate = () => {
    return (
      <div className="table-header">
        <div className="d-flex justify-content-between">
          {/* Search Input */}
          <div className="d-flex flex-row">
            <div className="me-3">
              <span className={classNames('p-input-icon-left', globalFilterValue ? 'p-input-icon-right' : '')}>
                <i className="pi pi-user" />
                <InputText
                  className="of-name-search"
                  style={{ width: '310px' }}
                  value={globalFilterValue}
                  onChange={(e) => onGlobalFilterChange(e.target.value)}
                  placeholder={t('input-texts.search-someone')}
                />
                {!!globalFilterValue && (
                  <i className="pi pi-times" style={{ cursor: 'pointer' }} onClick={() => onGlobalFilterChange('')} />
                )}
              </span>
            </div>
          </div>
          {/* Excel Export Button */}
          <div className="d-flex align-items-center">
            <div
              className="d-flex"
              style={{ cursor: 'pointer' }}
              onMouseOver={(e) => showExcel(e)}
              onMouseLeave={() => addToExcel.current?.hide()}
              onClick={exportExcel}>
              <i className="pi pi-file-excel excelIcon" style={{ fontSize: '25px' }}></i>
            </div>
            <OverlayPanel ref={addToExcel} className="add-excel-panel">
              <div className="add-excel-text">
                <p>{t('overlaypanel-texts.export-excel')}</p>
              </div>
            </OverlayPanel>
          </div>
        </div>
      </div>
    )
  }

  const reservationDateBodyTemplate = (rowData: IRemoteReservationResponse) => {
    let formatDate = moment(rowData.reservationDate).format('DD/MM/YYYY')
    return <span>{formatDate}</span>
  }
  const requestDateBodyTemplate = (rowData: IRemoteReservationResponse) => {
    let formatDate = moment(rowData.requestDate).format('DD/MM/YYYY')
    return <span>{formatDate}</span>
  }
  const statusBodyTemplate = (rowData: IRemoteReservationResponse) => {
    const lookup: Record<number, string> = {
      1: 'request-remote-reservation.pending-approval',
      2: 'request-remote-reservation.approved',
      3: 'request-remote-reservation.rejected',
    }
    return <span>{t(lookup[rowData.reservationStatusId])} </span>
  }

  const statusFilterTemplate = (options: { checked: any; value: any; filterCallback: (arg0: any) => void }) => {
    return (
      <Dropdown
        optionValue="value"
        optionLabel="label"
        value={options.value}
        options={isStatusOptions}
        onChange={(e) => options.filterCallback(e.value)}
        placeholder={t('request-remote-reservation.status')}
        className="p-column-filter filter-multiselect"
      />
    )
  }

  const footerLeftTemplate = () => {
    return (
      <div>
        <span>{t('visitor.between')} </span>
        <span className="font-bold">"{moment(startDate).format('DD/MM/YYYY')}"</span>
        <span> - </span>
        <span className="font-bold"> "{moment(endDate).format('DD/MM/YYYY')}" </span>
        {t('visitor.in-range')}
        <span className="font-bold"> {tableData?.length} </span>
        {t('visitor.record')} {t('visitor.record-listed')}
      </div>
    )
  }
  // Table Templates
  const templateBtn = (rowData: IRemoteReservationResponse) => {
    if (rowData.reservationStatusId !== EReservationStatus.PendingApproval) return
    return (
      <ApprovalButtonGroup>
        <TableApproveButton label={t('buttons.approve-text')} onClick={() => onApproveClick(rowData)} />
        <TableRejectButton label={t('buttons.reject')} onClick={() => onRejectClick(rowData)} />
        {/*<Button icon="pi pi-pencil" className="p-button-rounded create-btn" onClick={() => openModal('edit', rowData)} />*/}
      </ApprovalButtonGroup>
    )
  }
  const emptyMessageTemplate = () => {
    return (
      <div className="p-3 d-flex gap-2 flex-column justify-content-center align-items-center">
        <div
          role="img"
          aria-label="no-record-found"
          style={{
            width: 150,
            height: 150,
            backgroundColor: '#e8e8e8',
            WebkitMaskImage: `url(${noRecordImg})`,
            maskImage: `url(${noRecordImg})`,
            WebkitMaskRepeat: 'no-repeat',
            maskRepeat: 'no-repeat',
            WebkitMaskPosition: 'center',
            maskPosition: 'center',
            WebkitMaskSize: 'contain',
            maskSize: 'contain',
          }}
        />
        <span style={{ fontSize: '1.2rem', fontWeight: 700, color: '#e8e8e8' }}>
          {t('visitor.no-record-found')}
        </span>
      </div>
    )
  }

  return (
    <div className="mb-4">
      {/* DataTable */}
      <div className="tracing-table">
        <div className="card p-3">
          {/* Table Header - Search Input - Excel Export */}
          {tableHeaderTemplate()}
          <DataTable
            rowHover
            tableStyle={{ fontSize: '14px' }}
            value={tableData}
            loading={loading}
            filters={filters}
            emptyMessage={emptyMessageTemplate}
            globalFilterFields={['_nameSurname']}
            sortField="ReservationStartHour"
            sortOrder={-1}
            dataKey="id"
            paginator={hasItems}
            first={first}
            rows={rows}
            onPage={handlePageChange}
            rowsPerPageOptions={[5, 10, 20]}
            paginatorLeft={footerLeftTemplate}
            paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
            currentPageReportTemplate={
              t('visitor.from') +
              '{totalRecords}' +
              ' ' +
              t('visitor.record') +
              ' ' +
              t('visitor.in') +
              ' {first}. ' +
              t('visitor.and') +
              ' {last}. ' +
              t('visitor.records-shown')
            }
            responsiveLayout="scroll">
            <Column
              field="_nameSurname"
              header={`${t('visitor.name')} ${t('visitor.surname')}`}
              sortable
              style={{ minWidth: '10rem' }}
              bodyStyle={{ fontWeight: 'bold' }}
            />
            <Column field="email" header={t('visitor.email')} style={{ maxWidth: '20rem' }} />
            <Column
              align="center"
              field="requestDate"
              header={t('request-remote-reservation.request-date')}
              style={{ minWidth: '8rem' }}
              sortable
              sortField="requestDate"
              body={requestDateBodyTemplate}
            />
            <Column
              align="center"
              field="reservationDate"
              header={t('request-remote-reservation.reservation-date')}
              style={{ minWidth: '8rem' }}
              sortField="reservationDate"
              body={reservationDateBodyTemplate}
            />
            <Column
              align="center"
              field="notificationCount"
              header={t('request-remote-reservation.request-count')}
              style={{ minWidth: '8rem' }}
            />
            <Column
              align="center"
              field="reservationStatusId"
              header={t('request-remote-reservation.status')}
              filter
              showFilterMatchModes={false}
              showFilterMenuOptions={false}
              filterElement={statusFilterTemplate}
              style={{ maxWidth: '8rem' }}
              body={statusBodyTemplate}
            />
            <Column body={templateBtn} />
          </DataTable>

          <NotificationDialog
            display={rejectNotification}
            imgSrc={warningImage}
            goHomeButton
            extraButton
            title={t('overlaypanel-texts.request-remote-reservation-reject')}
            content={t('overlaypanel-texts.request-remote-reservation-reject-text', {
              notificationInfo: formatNotificationText(selectedRow?.notificationCount),
            })}
            firstButtonLabel={t('buttons.reject')}
            secondButtonLabel={t('buttons.cancel')}
            onHide={() => setRejectNotification(false)}
            onSubmit={() => handleConfirmIsCanceled(selectedRow)}
            selectedType={null}
            setSelectedType={undefined}
            types={[]}
            inputFieldVisible={true} // Textbox'un görünmesini sağlar
            inputFieldLabel="Reddetme nedeninizi yazınız" // Dinamik placeholder
            inputValue={inputValue}
            onInputChange={(val) => {
              setInputValue(val)
              if (val.trim()) setRejectValidationError(false)
            }}
            inputError={rejectValidationError}
            loading={actionLoading}
          />

          <NotificationDialog
            display={approveNotification}
            imgSrc={warningImage}
            goHomeButton
            extraButton
            title={t('overlaypanel-texts.request-remote-reservation-approve')}
            content={t('overlaypanel-texts.request-remote-reservation-text', {
              notificationInfo: formatNotificationText(selectedRow?.notificationCount),
            })}
            firstButtonLabel={t('buttons.yes')}
            secondButtonLabel={t('buttons.no')}
            onHide={() => setApproveNotification(false)}
            onSubmit={handleConfirmApprove}
            selectedType={null}
            setSelectedType={undefined}
            types={[]}
            loading={actionLoading}
          />
        </div>
      </div>
    </div>
  )
}
